import { shipmentApi } from "#/api/shipmentApi";
import ActionButton from "#/components/buttons/ActionButton";
import Pagination from "#/components/buttons/Pagination";
import { showSuccessNotification } from "#/components/notifications";
import BaseTable, { type TableSortProps } from "#/components/tables/BaseTable";
import type { Shipment } from "#/types/shipment";
import { getRandomColor } from "#/utils/ColorUtils";
import { Badge, Button, Modal, Table, TextInput, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconEye, IconSearch, IconTrash } from "@tabler/icons-react";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { useClient } from "#/hook/useClient";
import { formatDate, formatTime } from "#/utils/dateTimeUtils";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePermission } from "#/hooks/usePermission";
import PermissionTooltip from "#/components/ui/PermissionTooltip";
import { useTranslation } from "react-i18next";

const ShipmentLogsPage = () => {
  const navigate = useNavigate();
  const { client } = useClient();
  const { canDelete } = usePermission();
  const { t } = useTranslation();
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(25);
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("DESC");
  const [search, setSearch] = useState<string>("");
  const [
    deleteModalOpened,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure(false);
  const [shipmentToDelete, setShipmentToDelete] = useState<Shipment>();

  const headers = [
    { key: "id", value: t("shipments.columns.id") },
    { key: "label", value: t("shipments.columns.label") },
    { key: "vehicle", value: t("shipments.columns.vehicle") },
    { key: "items", value: t("shipments.columns.items") },
    { key: "location", value: t("shipments.columns.location") },
    { key: "scanner", value: t("shipments.columns.scanner") },
    { key: "date", value: t("shipments.columns.date") },
    { key: "updatedAt", value: t("shipments.columns.lastUpdated") },
    { key: "actions", value: t("common.actions") },
  ];
  const [sortBy, setSortBy] = useState<string>("createdAt");

  const {
    data: { items = [], total = 0, per_page = 0 } = {},
    isPending,
    refetch: getShipments,
  } = useQuery({
    queryKey: ["shipments", sortBy, page, sortDirection, search],
    queryFn: async () => {
      try {
        const response = await shipmentApi.getAll({
          limit: limit,
          page: page,
          ...(sortBy ? { sortBy: sortBy } : {}),
          ...(sortDirection ? { order: sortDirection } : {}),
          ...(search ? { search: search } : {}),
        });
        console.log("🚀 ~ ShipmentLogsPage ~ response:", response);
        return {
          items: response.data.data,
          total: response.data.total,
          per_page: response.data.limit,
        };
      } catch (error) {
        console.log("🚀 ~ queryFn: ~ error:", error);
        throw new Error("No shipments returned");
      }
    },
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60,
    retry: false,
  });

  const deleteShipmentMutation = useMutation({
    mutationFn: async (id: number) => {
      return await shipmentApi.delete(id);
    },
    onSuccess: () => {
      showSuccessNotification({
        message: "Shipment deleted successfully",
      });
      closeDeleteModal();
      setShipmentToDelete(undefined);
      void getShipments();
    },
    onError: (error) => {
      console.log("🚀 ~ deleteShipment ~ error:", error);
    },
  });

  return (
    <div className="w-full h-full flex flex-col overflow-hidden gap-4">
      <div className="flex items-center justify-between bg-white z-10">
        <Title order={2}>{t("shipments.title")}</Title>
        <div className="w-fit flex items-center justify-center gap-4">
          <TextInput
            placeholder={t("shipments.searchPlaceholder")}
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="w-80"
          />
          <Pagination
            page={page}
            total={total}
            limit={per_page}
            onChange={setPage}
            onLimitChange={(newLimit) => {
              setLimit(newLimit);
              setPage(1);
            }}
          />
        </div>
      </div>
      <div className="flex-1 overflow-auto">
        <BaseTable
          headers={headers.map((item) => item.value)}
          loading={isPending}
          allowSorting={true}
          sortableColumns={[1, 2, 4, 5, 6, 7]}
          onSortChange={(e: TableSortProps) => {
            setPage(1);
            setSortDirection(e.direction);
            setSortBy(headers[e.index].key);
          }}
          defaultSort={{
            direction: "DESC",
            index: 7,
          }}
          rows={items.map((item) => {
            return (
              <Table.Tr key={item.id}>
                <Table.Td style={{ minWidth: '100px' }}>{item.id ?? "--"}</Table.Td>
                <Table.Td style={{ minWidth: '150px' }}>{item.label ?? "--"}</Table.Td>
                <Table.Td style={{ minWidth: '180px' }}>
                  {item.vehicle ? (
                    <div className="flex flex-col gap-1">
                      <span>{item.vehicle.name}</span>
                      {item.vehicle.rfid_tag?.rfid && (
                        <Badge
                          color={getRandomColor(item.vehicle.rfid_tag.rfid)}
                          variant="light"
                          size="sm"
                        >
                          {item.vehicle.rfid_tag.rfid}
                        </Badge>
                      )}
                    </div>
                  ) : (
                    "--"
                  )}
                </Table.Td>
                <Table.Td style={{ minWidth: '100px' }}>{item.items?.length ?? 0}</Table.Td>
                <Table.Td className="max-w-xs truncate" style={{ minWidth: '200px' }} title={item.location}>
                  {item.location ?? "--"}
                </Table.Td>
                <Table.Td style={{ minWidth: '150px' }}>{item.scanner?.name ?? "--"}</Table.Td>
                <Table.Td style={{ minWidth: '120px' }}>
                  {item.date ? formatDate(item.date, client) : "--"}
                </Table.Td>
                <Table.Td style={{ minWidth: '180px' }}>
                  {item.updatedAt ? (
                    <span>
                      {formatDate(item.updatedAt, client)}
                      <br />
                      {formatTime(item.updatedAt, client)}
                    </span>
                  ) : (
                    "--"
                  )}
                </Table.Td>
                <Table.Td style={{ minWidth: '120px' }}>
                  <div className="w-full flex items-center justify-center gap-2">
                    <ActionButton
                      icon={IconEye}
                      variant="outline"
                      onClick={() => {
                        void navigate(`/fleet/logs/${item.id}`);
                      }}
                    />
                    <PermissionTooltip disabled={!canDelete()}>
                      <ActionButton
                        icon={IconTrash}
                        variant="outline"
                        color="red"
                        onClick={() => {
                          setShipmentToDelete(item);
                          openDeleteModal();
                        }}
                        disabled={!canDelete()}
                      />
                    </PermissionTooltip>
                  </div>
                </Table.Td>
              </Table.Tr>
            );
          })}
        />
      </div>

      <Modal
        opened={deleteModalOpened}
        onClose={closeDeleteModal}
        centered
        title={t("shipments.deleteShipment.title")}
        closeOnClickOutside={false}
        classNames={{
          title: "font-bold",
        }}
      >
        <div className="w-full flex flex-col items-center justify-center gap-4">
          <div className="flex flex-col gap-2 text-sm">
            <span>{t("shipments.deleteShipment.description")}</span>
            <div className="flex flex-col gap-1 mx-auto my-4 bg-slate-100 border border-dashed border-slate-400 rounded-lg p-4 w-full text-center">
              <span className="font-bold">{shipmentToDelete?.label ?? "--"}</span>
              <span className="text-xs text-gray-600">
                Vehicle: {shipmentToDelete?.vehicle?.name ?? "--"} (
                {shipmentToDelete?.vehicle?.rfid ?? "--"})
              </span>
              <span className="text-xs text-gray-600">
                Items: {shipmentToDelete?.items?.length ?? 0}
              </span>
              <span className="text-xs text-gray-600">
                Location: {shipmentToDelete?.location ?? "--"}
              </span>
            </div>
            <span>
              {t("shipments.deleteShipment.warning")}
            </span>

            <span className="font-bold my-2">
              {t("shipments.deleteShipment.confirmation")}
            </span>
          </div>

          <div className="w-full grid grid-cols-[auto_1fr] items-center gap-2">
            <Button size="sm" color="black" onClick={closeDeleteModal}>
              {t("common.cancel")}
            </Button>
            <PermissionTooltip disabled={!canDelete()}>
              <Button
                onClick={() => {
                  if (shipmentToDelete) {
                    deleteShipmentMutation.mutate(shipmentToDelete.id);
                  }
                }}
                color="red"
                size="sm"
                loading={deleteShipmentMutation.isPending}
                leftSection={<IconTrash size={18} />}
                disabled={!canDelete()}
              >
                {t("shipments.deleteShipment.confirmButton")}
              </Button>
            </PermissionTooltip>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ShipmentLogsPage;
